import { getFirstLastNameObj } from '../../../../utils/format';
import { useTranslation } from 'react-i18next';
import { store, useAppDispatch, useAppSelector } from '../../../../store';
import { invitationActions, selectInvitation } from '../../../../store/entities/invitation';
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar';
import { useState } from 'react';
import { ErrorStateType } from '../../../../models/common';
import { invitationListActions } from '../../../../store/entities/invitationList';
import { invitationHistoryListActions } from '../../../../store/entities/invitationHistoryList';
import { invitationStateAudActions } from '../../../../store/entities/invitationStateAud';
import { invitationTypeVisitListActions } from '../../../../store/entities/invitationTypeVisitList';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { InvitationType } from '@api/models/enums/InvitationType';
import { CreateUpdateInvitation } from '@api/models/invitation/CreateUpdateInvitation';
import { invitationPersonService, invitationService } from '@api/services/services';
import { buildInvitationPersonQuery, invitationPersonListActions } from '../../../../store/entities/invitationPersonList';

export const invitationGetUpdateDto = (invitation: InvitationDto) => {
  const res: CreateUpdateInvitation = {
    activities:
      invitation.activities?.map((x) => ({
        id: x.activity?.id ?? '',
        name: x.activity?.name,
        nameEn: x.activity?.nameEn,
      })) ?? [],
    attachments: invitation.attachments?.map((x) => x.id ?? ''),
    employee: {
      id: invitation.employee?.id ?? '',
      name: getFirstLastNameObj(invitation.employee),
    },
    name: invitation.name ?? '',
    note: invitation.note,
    noteInternal: invitation.noteInternal,
    responsiblePerson: invitation.responsiblePerson!,
    supplier: invitation.supplier,
    validFrom: invitation.validFrom!,
    validTo: invitation.validTo,
    disableVisitationNotification: invitation.disableVisitationNotification,
    createHandoverProtocol: invitation.createHandoverProtocol,
    turnstilesActive: invitation.turnstilesActive,
    workplaces:
      invitation.workplaces?.map((x) => ({
        id: x.workplace?.id ?? '',
        name: x.workplace?.name,
        nameEn: x.workplace?.nameEn,
      })) ?? [],
    notifyEmployees: invitation.isNotifyEmployees ? invitation.notifyEmployees : [],
    earliestEntryTime: invitation.earliestEntryTime,
    entryRequestRequired: invitation.entryRequestRequired,
  };
  return res;
};

export const invitationLock = {
  locked: false,
};

export const useInvitation = () => {
  const { t, i18n } = useTranslation();
  const invitation = useAppSelector(selectInvitation);
  const dispatch = useAppDispatch();
  const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useAppSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<ErrorStateType>();

  const isOneTime = invitation?.type === InvitationType.OneTime || invitation?.type === InvitationType.OneTimeEntry;

  const lockInvitation = () => {
    invitationLock.locked = true;
  };

  const reloadInvitation = async () => {
    const [dataResponse, personsResponse] = await Promise.all([
      invitationService.getById(invitation.id!),
      invitationPersonService.query(buildInvitationPersonQuery(invitation.id!, store.getState())),
    ]);
    dataResponse.data.persons = personsResponse.data.content;

    try {
      const auditResponse = await invitationService.getInvitationAudit(invitation.id!, { page: 0, size: 1 });
      dispatch(invitationStateAudActions.success(auditResponse.data.content?.[0]));
    } catch (err) {
      dispatch(invitationStateAudActions.error());
    }

    dispatch(invitationActions.success(dataResponse.data));
    dispatch(invitationPersonListActions.success(personsResponse.data));
    dispatch(invitationListActions.reload(true));
    dispatch(invitationHistoryListActions.reload(true));
    dispatch(invitationTypeVisitListActions.reload(true));
  };

  const unlockInvitation = () => {
    invitationLock.locked = false;
  };

  const callWithLock = async (fn: () => Promise<void>) => {
    lockInvitation();
    try {
      await fn();
    } finally {
      unlockInvitation();
    }
  };

  return {
    t,
    i18n,
    invitation,
    lockInvitation,
    reloadInvitation,
    unlockInvitation,
    callWithLock,
    dispatch,
    enqueueSuccessSnackbar,
    enqueueErrorSnackbar,
    isSaving,
    setIsSaving,
    saveError,
    setSaveError,
    isOneTime,
  };
};
